<template>
  <v-container fluid>
    <v-card outlined class="mb-6">
      <v-card-title>
        <v-icon left small>mdi-post</v-icon> Blog Management
        <v-spacer />
        <v-btn color="primary" @click="openBlogEditor()">
          <v-icon left>mdi-plus</v-icon> Add New Blog
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Blog List -->
        <div v-if="blogs.length === 0" class="text-center py-8">
          <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-post-outline</v-icon>
          <h3 class="text-h6 grey--text mb-2">No blogs yet</h3>
          <p class="text-body-2 grey--text mb-4">Create your first blog post to start sharing content with your audience.</p>
          <v-btn color="primary" @click="openBlogEditor()">
            <v-icon left>mdi-plus</v-icon> Create First Blog
          </v-btn>
        </div>

        <v-row v-else>
          <v-col 
            v-for="blog in blogs" 
            :key="blog.id" 
            cols="12" 
            md="6" 
            lg="4"
          >
            <v-card outlined class="h-100">
              <!-- Blog Image -->
              <v-img
                v-if="blog.image"
                :src="blog.image"
                height="200"
                cover
                class="grey lighten-2"
              >
                <template v-slot:placeholder>
                  <v-row class="fill-height ma-0" align="center" justify="center">
                    <v-progress-circular indeterminate color="grey lighten-1"></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
              <div v-else class="grey lighten-2 d-flex align-center justify-center" style="height: 200px;">
                <v-icon size="48" color="grey lighten-1">mdi-image-outline</v-icon>
              </div>

              <!-- Blog Content -->
              <v-card-text>
                <div class="d-flex align-center justify-space-between mb-2">
                  <v-chip
                    :color="blog.status === 'published' ? 'green' : 'orange'"
                    small
                    text-color="white"
                  >
                    {{ blog.status === 'published' ? 'Published' : 'Draft' }}
                  </v-chip>
                  <v-menu bottom left>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small v-bind="attrs" v-on="on">
                        <v-icon>mdi-dots-vertical</v-icon>
                      </v-btn>
                    </template>
                    <v-list dense>
                      <v-list-item @click="editBlog(blog)">
                        <v-list-item-icon>
                          <v-icon small>mdi-pencil</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>Edit</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="toggleBlogStatus(blog)">
                        <v-list-item-icon>
                          <v-icon small>{{ blog.status === 'published' ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title>{{ blog.status === 'published' ? 'Unpublish' : 'Publish' }}</v-list-item-title>
                      </v-list-item>
                      <v-divider></v-divider>
                      <v-list-item @click="deleteBlog(blog.id)" class="error--text">
                        <v-list-item-icon>
                          <v-icon small color="error">mdi-delete</v-icon>
                        </v-list-item-icon>
                        <v-list-item-title class="error--text">Delete</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </div>

                <h3 class="text-h6 mb-2">{{ blog.title }}</h3>
                <div v-if="blog.text" class="text-body-2 grey--text mb-3 blog-content" v-html="sanitizeAndTruncateHtml(blog.text, 150)"></div>
                <div v-else class="text-body-2 grey--text mb-3">No content available</div>

                <div class="d-flex align-center justify-space-between">
                  <div class="caption grey--text">
                    <v-icon x-small class="mr-1">mdi-calendar-clock</v-icon>
                    {{ formatDate(blog.publishedAt) }}
                  </div>
                  <div class="caption grey--text">
                    <v-icon x-small class="mr-1">mdi-text</v-icon>
                    {{ getTextLength(blog.text) }} chars
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Blog Dialog -->
    <v-dialog v-model="blogDialog" max-width="800px" persistent>
      <v-card>
        <v-card-title>
          <v-icon left>mdi-post</v-icon>
          {{ editingBlog ? 'Edit Blog' : 'Create New Blog' }}
        </v-card-title>

        <v-card-text>
          <v-form ref="blogForm" lazy-validation>
            <v-row>
              <!-- Blog Image -->
              <v-col cols="12">
                <v-card outlined class="pa-3 mb-4" light>
                  <div class="d-flex align-center justify-space-between mb-3">
                    <div class="subtitle-2 font-weight-bold">
                      <v-icon small color="primary" class="mr-1">mdi-image</v-icon>
                      Blog Image (Optional)
                    </div>
                    <v-btn
                      v-if="blogData.image"
                      small
                      text
                      color="error"
                      @click="removeBlogImage"
                    >
                      <v-icon left x-small>mdi-delete</v-icon>
                      Remove
                    </v-btn>
                  </div>
                  
                  <div class="d-flex align-center">
                    <!-- Image Preview -->
                    <div v-if="blogData.image" class="mr-4">
                      <v-img
                        :src="blogData.image"
                        alt="Blog Image"
                        width="120"
                        height="80"
                        class="rounded-lg"
                        cover
                      />
                    </div>
                    
                    <!-- Upload Button -->
                    <div v-if="!blogData.image">
                      <v-btn
                        @click="initCloudinaryWidget"
                        color="primary"
                        outlined
                        small
                      >
                        <v-icon left x-small>mdi-upload</v-icon>
                        Upload Image
                      </v-btn>
                      <div class="caption grey--text mt-1">
                        Optional: Add a featured image for your blog
                      </div>
                    </div>
                  </div>
                </v-card>
              </v-col>

              <!-- Blog Title -->
              <v-col cols="12">
                <v-text-field
                  v-model="blogData.title"
                  label="Blog Title"
                  :rules="[rules.required]"
                  outlined
                  dense
                  placeholder="Enter your blog title"
                  hint="A compelling title that captures your audience's attention"
                  persistent-hint
                />
              </v-col>

              <!-- Blog Content -->
              <v-col cols="12">
                <v-textarea
                  v-model="blogData.text"
                  label="Blog Content"
                  :rules="[rules.required, rules.maxWords]"
                  auto-grow
                  rows="8"
                  outlined
                  :counter="500"
                  placeholder="Write your blog content here..."
                  hint="Share valuable content with your audience (max 500 words)"
                  persistent-hint
                />
                <div class="caption grey--text mt-1">
                  {{ wordCount }} words (max 500)
                </div>
              </v-col>

              <!-- Published Date -->
              <v-col cols="12" md="6">
                <v-menu
                  v-model="dateMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="blogData.publishedDate"
                      label="Published Date"
                      prepend-inner-icon="mdi-calendar"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                      :rules="[rules.required]"
                    />
                  </template>
                  <v-date-picker
                    v-model="blogData.publishedDate"
                    @input="dateMenu = false"
                  />
                </v-menu>
              </v-col>

              <!-- Published Time -->
              <v-col cols="12" md="6">
                <v-menu
                  v-model="timeMenu"
                  :close-on-content-click="false"
                  :nudge-right="40"
                  transition="scale-transition"
                  offset-y
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      v-model="blogData.publishedTime"
                      label="Published Time"
                      prepend-inner-icon="mdi-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      outlined
                      dense
                      :rules="[rules.required]"
                    />
                  </template>
                  <v-time-picker
                    v-model="blogData.publishedTime"
                    @click:minute="timeMenu = false"
                    format="24hr"
                  />
                </v-menu>
              </v-col>

              <!-- DateTime Preview -->
              <v-col cols="12">
                <v-card outlined class="pa-3" light>
                  <div class="subtitle-2 font-weight-bold mb-2">
                    <v-icon small color="primary" class="mr-1">mdi-calendar-clock</v-icon>
                    Publish Date & Time Preview
                  </div>
                  <div class="body-2 grey--text">
                    {{ formattedPublishedDateTime || 'Select date and time' }}
                  </div>
                </v-card>
              </v-col>

              <!-- Status -->
              <v-col cols="12" md="6">
                <v-select
                  v-model="blogData.status"
                  :items="statusOptions"
                  label="Status"
                  outlined
                  dense
                  :rules="[rules.required]"
                />
              </v-col>

              <!-- SEO Fields -->
              <v-col cols="12">
                <v-text-field
                  v-model="blogData.seoTitle"
                  label="SEO Title (Optional)"
                  outlined
                  dense
                  placeholder="SEO optimized title for search engines"
                  hint="Leave empty to use the blog title"
                  persistent-hint
                  :counter="60"
                />
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="blogData.seoDescription"
                  label="SEO Description (Optional)"
                  auto-grow
                  rows="2"
                  outlined
                  dense
                  placeholder="Brief description for search engines"
                  hint="Leave empty to auto-generate from content"
                  persistent-hint
                  :counter="160"
                />
              </v-col>

              <!-- Tags -->
              <v-col cols="12">
                <v-combobox
                  v-model="blogData.tags"
                  :items="suggestedTags"
                  label="Tags (Optional)"
                  multiple
                  chips
                  deletable-chips
                  small-chips
                  outlined
                  dense
                  placeholder="Add tags to help categorize your blog"
                  hint="Press Enter to add custom tags"
                  persistent-hint
                />
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="justify-end pa-4">
          <v-btn text @click="closeBlogDialog">Cancel</v-btn>
          <v-btn 
            color="primary" 
            @click="saveBlog"
            :loading="saving"
            :disabled="saving"
          >
            {{ editingBlog ? 'Update Blog' : 'Create Blog' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="400px">
      <v-card>
        <v-card-title class="text-h6">Delete Blog</v-card-title>
        <v-card-text>
          Are you sure you want to delete this blog? This action cannot be undone.
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="confirmDelete" :loading="deleting">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { db } from "@/utils/firebase.utils";
import {
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  orderBy,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";

import commonMixins from "@/mixins/common.mixins.js";
import DOMPurify from "dompurify";

export default {
  name: "BlogManagement",
  mixins: [commonMixins],
  data() {
    return {
      blogs: [],
      blogDialog: false,
      deleteDialog: false,
      editingBlog: null,
      saving: false,
      deleting: false,
      dateMenu: false,
      timeMenu: false,
      blogToDelete: null,
      
      blogData: {
        title: "",
        text: "",
        image: "",
        publishedDate: "",
        publishedTime: "",
        status: "draft",
        seoTitle: "",
        seoDescription: "",
        tags: [],
      },

      statusOptions: [
        { text: "Draft", value: "draft" },
        { text: "Published", value: "published" },
      ],

      suggestedTags: [
        "Business",
        "Marketing",
        "Tips",
        "News",
        "Tutorial",
        "Guide",
        "Industry",
        "Technology",
        "Customer Service",
        "Product",
        "Service",
      ],

      rules: {
        required: (v) => !!v || "Required",
        maxWords: (v) => {
          if (!v) return true;
          
          // Create a temporary div to parse HTML content
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = v;
          
          // Get text content without HTML tags
          const textContent = tempDiv.textContent || tempDiv.innerText || '';
          const words = textContent.trim().split(/\s+/).filter(word => word.length > 0);
          return words.length <= 500 || `Maximum 500 words allowed (currently using ${words.length} words)`;
        },
        futureDate: (v) => {
          if (!v) return true;
          const selectedDateTime = new Date(`${this.blogData.publishedDate}T${this.blogData.publishedTime}:00`);
          const now = new Date();
          return selectedDateTime >= now || "Publish date must be in the future";
        },
      },

      // Cloudinary Widget Properties
      cloudinaryWidget: null,
    };
  },

  computed: {
    wordCount() {
      if (!this.blogData.text) return 0;
      
      // Create a temporary div to parse HTML content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = this.blogData.text;
      
      // Get text content without HTML tags
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      const words = textContent.trim().split(/\s+/).filter(word => word.length > 0);
      return words.length;
    },
    formattedPublishedDateTime() {
      if (!this.blogData.publishedDate || !this.blogData.publishedTime) return '';
      const dateTime = new Date(`${this.blogData.publishedDate}T${this.blogData.publishedTime}:00`);
      return dateTime.toLocaleString();
    },
  },

  async created() {
    await this.fetchBlogs();
    this.loadCloudinaryWidget();
  },

  methods: {
    async fetchBlogs() {
      try {
        const blogsRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "blogs"
        );
        
        const q = query(blogsRef, orderBy("createdAt", "desc"));
        const querySnapshot = await getDocs(q);
        
        this.blogs = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          publishedAt: doc.data().publishedAt ? new Date(doc.data().publishedAt) : null,
        }));
      } catch (error) {
        console.error("Error fetching blogs:", error);
        this.$snackbar.show("Failed to load blogs");
      }
    },

    openBlogDialog(blog = null) {
      this.editingBlog = blog;
      if (blog) {
        // Edit mode
        const publishedDate = blog.publishedAt ? new Date(blog.publishedAt) : new Date();
        this.blogData = {
          title: blog.title || "",
          text: blog.text || "",
          image: blog.image || "",
          publishedDate: publishedDate.toISOString().substr(0, 10),
          publishedTime: publishedDate.toTimeString().substr(0, 5),
          status: blog.status || "draft",
          seoTitle: blog.seoTitle || "",
          seoDescription: blog.seoDescription || "",
          tags: blog.tags || [],
        };
      } else {
        // Create mode
        const currentDateTime = this.getCurrentDateTime();
        this.blogData = {
          title: "",
          text: "",
          image: "",
          publishedDate: currentDateTime.date,
          publishedTime: currentDateTime.time,
          status: "draft",
          seoTitle: "",
          seoDescription: "",
          tags: [],
        };
      }
      this.blogDialog = true;
    },

    closeBlogDialog() {
      this.blogDialog = false;
      this.editingBlog = null;
      this.$refs.blogForm?.resetValidation();
    },

    async saveBlog() {
      try {
        const valid = await this.$refs.blogForm.validate();
        if (!valid) {
          this.$snackbar.show("Please complete all required fields");
          return;
        }

        this.saving = true;

        // Combine date and time into a single datetime
        const publishedDateTime = new Date(`${this.blogData.publishedDate}T${this.blogData.publishedTime}:00`);
        
        // Validate future date for published status
        if (this.blogData.status === 'published' && publishedDateTime < new Date()) {
          this.$snackbar.show("Cannot publish with a past date/time. Please select a future date and time.");
          return;
        }
        
        const blogData = {
          title: this.blogData.title.trim(),
          text: this.blogData.text.trim(),
          image: this.blogData.image,
          publishedAt: publishedDateTime.toISOString(),
          status: this.blogData.status,
          seoTitle: this.blogData.seoTitle.trim() || this.blogData.title.trim(),
          seoDescription: this.blogData.seoDescription.trim(),
          tags: this.blogData.tags,
          updatedAt: serverTimestamp(),
        };

        if (this.editingBlog) {
          // Update existing blog
          const blogRef = doc(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.WID,
            "blogs",
            this.editingBlog.id
          );
          await updateDoc(blogRef, blogData);
          this.$snackbar.show("Blog updated successfully");
        } else {
          // Create new blog
          blogData.createdAt = serverTimestamp();
          const blogsRef = collection(
            db,
            "users",
            this.currentUID,
            "fly_workspaces",
            this.WID,
            "blogs"
          );
          await addDoc(blogsRef, blogData);
          this.$snackbar.show("Blog created successfully");
        }

        this.closeBlogDialog();
        await this.fetchBlogs();
      } catch (error) {
        console.error("Error saving blog:", error);
        this.$snackbar.show("Failed to save blog");
      } finally {
        this.saving = false;
      }
    },

    editBlog(blog) {
      this.$router.push(`/blog-editor/${blog.id}`);
    },

    openBlogEditor() {
      this.$router.push('/blog-editor');
    },

    async toggleBlogStatus(blog) {
      try {
        const newStatus = blog.status === 'published' ? 'draft' : 'published';
        const blogRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "blogs",
          blog.id
        );
        
        await updateDoc(blogRef, {
          status: newStatus,
          updatedAt: serverTimestamp(),
        });
        
        this.$snackbar.show(`Blog ${newStatus === 'published' ? 'published' : 'unpublished'} successfully`);
        await this.fetchBlogs();
      } catch (error) {
        console.error("Error updating blog status:", error);
        this.$snackbar.show("Failed to update blog status");
      }
    },

    deleteBlog(blogId) {
      this.blogToDelete = blogId;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      try {
        this.deleting = true;
        
        const blogRef = doc(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "blogs",
          this.blogToDelete
        );
        
        await deleteDoc(blogRef);
        
        this.$snackbar.show("Blog deleted successfully");
        this.deleteDialog = false;
        this.blogToDelete = null;
        await this.fetchBlogs();
      } catch (error) {
        console.error("Error deleting blog:", error);
        this.$snackbar.show("Failed to delete blog");
      } finally {
        this.deleting = false;
      }
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
    },

    formatDate(dateString) {
      if (!dateString) return "No date";
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    },

    removeBlogImage() {
      this.blogData.image = "";
    },

    getCurrentDateTime() {
      const now = new Date();
      return {
        date: now.toISOString().substr(0, 10),
        time: now.toTimeString().substr(0, 5)
      };
    },

    sanitizeHtml(html) {
      if (!html) return "";
      return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'strong', 'em', 'b', 'i', 'u', 'br', 'div', 'span', 'a'],
        ALLOWED_ATTR: ['href', 'target', 'class', 'style'],
        ALLOW_DATA_ATTR: false
      });
    },

    sanitizeAndTruncateHtml(html, maxLength) {
      if (!html) return "";
      
      // First sanitize the HTML
      const sanitizedHtml = this.sanitizeHtml(html);
      
      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = sanitizedHtml;
      
      // Get the text content to check length
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      
      // If no text content after sanitization, return empty string
      if (!textContent.trim()) {
        return "";
      }
      
      if (textContent.length <= maxLength) {
        return sanitizedHtml;
      }
      
      // Truncate the text content
      const truncatedText = textContent.substring(0, maxLength) + '...';
      
      // Create a simple paragraph with the truncated text
      return `<p>${truncatedText}</p>`;
    },

    getTextLength(html) {
      if (!html) return 0;
      
      // Create a temporary div to parse the HTML
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = html;
      
      // Get the text content without HTML tags
      const textContent = tempDiv.textContent || tempDiv.innerText || '';
      return textContent.length;
    },


    // Cloudinary Widget Methods
    loadCloudinaryWidget() {
      if (!window.cloudinary) {
        const script = document.createElement("script");
        script.src = "https://upload-widget.cloudinary.com/global/all.js";
        script.onload = () => {
          console.log("Cloudinary widget loaded");
        };
        document.head.appendChild(script);
      }
    },

    initCloudinaryWidget() {
      if (!window.cloudinary) {
        this.loadCloudinaryWidget();
        setTimeout(() => this.initCloudinaryWidget(), 1000);
        return;
      }

      this.cloudinaryWidget = window.cloudinary.createUploadWidget(
        {
          cloudName: process.env.VUE_APP_CLOUDINARY_CLOUD_NAME,
          uploadPreset: "demo_preset",
          sources: [
            "local",
            "camera",
            "url",
            "dropbox",
            "image_search",
            "shutterstock",
            "gettyimages",
            "istock",
            "unsplash",
            "google_drive",
          ],
          accept: "image/*",
          clientAllowedFormats: ["png", "jpg", "jpeg", "heic", "heif", "webp"],
          multiple: false,
          maxImageFileSize: 20000000,
          tags: [`${this.currentUID}_${this.WID}`],
          folder: `users/${this.currentUID}/${this.WID}/blogs`,
        },
        (error, result) => {
          if (!error && result && result.event === "success") {
            this.blogData.image = result.info.secure_url;
          }
        }
      );

      this.cloudinaryWidget.open();
    },
  },
};
</script>

<style scoped>
.v-card {
  transition: transform 0.2s ease-in-out;
}

.v-card:hover {
  transform: translateY(-2px);
}

.v-chip {
  cursor: pointer;
}

/* Blog content styling */
.blog-content {
  line-height: 1.5;
  overflow: hidden;
}

.blog-content p {
  margin-bottom: 0.5rem;
}

.blog-content p:last-child {
  margin-bottom: 0;
}

.blog-content h1, .blog-content h2, .blog-content h3,
.blog-content h4, .blog-content h5, .blog-content h6 {
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.blog-content ul, .blog-content ol {
  margin: 0.5rem 0;
  padding-left: 1.5rem;
}

.blog-content li {
  margin-bottom: 0.25rem;
}

.blog-content a {
  color: #1976d2;
  text-decoration: none;
}

.blog-content a:hover {
  text-decoration: underline;
}

.blog-content strong, .blog-content b {
  font-weight: 600;
}

.blog-content em, .blog-content i {
  font-style: italic;
}
</style> 